<template>
  <div class="wrapper__content-sale-document col-container">
    <!-- Header -->
    <div class="col-header">
      <div class="col-header-left">
        <ButtonCirclePlusComponent
          v-if="$can('expenses.manage')"
          @onClick="openForm"
        />
        <span class="col-title">Gastos</span>
      </div>
      <div class="col-actions">
        <ButtonFilterComponent
          @onChangeFilters="onChangeFilters"
          @onApplyFilters="onApplyFilters"
          :setFilters="searchFilters"
          :loading="loading"
          placeholder="Buscar por título"
          isSearch
          isOrderBy
          isDates
        />
      </div>
    </div>

    <!-- Lista de gastos como cards -->
    <WrapperScrollbarComponent>
      <template v-if="loading">
        <SkeletonOneCardComponent v-for="n in 4" :key="n" />
      </template>

      <template v-else>
        <ExpenseCardComponent
          v-for="expense in expenses"
          :key="expense.id"
          :expense="expense"
          @onClick="openEdit"
          @onDelete="confirmDelete"
        />

        <div v-if="!expenses.length" class="col-empty">
          <div class="empty-icon">
            <i class="el-icon-money" style="font-size: 18px"></i>
          </div>
          <p class="empty-title">Sin gastos</p>
          <p class="empty-sub">No hay gastos registrados</p>
          <button
            v-if="$can('expenses.manage')"
            class="empty-cta"
            @click="openForm"
          >
            + Agregar gasto
          </button>
        </div>
      </template>
    </WrapperScrollbarComponent>

    <!-- Modal nuevo / editar gasto -->
    <ExpenseFormModalComponent
      :show="showForm"
      :edit-expense="editExpense"
      @close="
        showForm = false;
        editExpense = null;
      "
      @saved="onExpenseSaved"
    />
  </div>
</template>

<script>
import ButtonCirclePlusComponent from "../../../components/ButtonCirclePlus";
import ButtonFilterComponent from "../../../components/DrawerFilter";
import WrapperScrollbarComponent from "../../../../components/WrapperScrollbar";
import ExpenseCardComponent from "./ExpenseCard";
import ExpenseFormModalComponent from "./ExpenseFormModal";
import SkeletonOneCardComponent from "@/components/Animations/Loading/SkeletonOneCard";
import projectService from "../../projects/services/projectService";

export default {
  name: "ExpenseColumnsComponent",
  components: {
    ButtonCirclePlusComponent,
    ButtonFilterComponent,
    WrapperScrollbarComponent,
    ExpenseCardComponent,
    ExpenseFormModalComponent,
    SkeletonOneCardComponent
  },
  data() {
    return {
      loading: false,
      showForm: false,
      editExpense: null,
      expenses: [],
      totals: { direct_total: 0, overhead_total: 0, grand_total: 0 },
      searchFilters: {}
    };
  },
  mounted() {
    // Por defecto la columna muestra SOLO el mes en curso — sin esto se
    // traían todos los gastos históricos sin filtrar, y como no había
    // gastos nuevos cargados, la vista quedaba dominada por el mes con más
    // registros (ej. Abril) en vez de resetearse cada mes como las demás
    // columnas del tablero (facturas, cotizaciones, etc.).
    this.setDefaultCurrentMonth();
    this.loadExpenses(this.buildParams(this.searchFilters));
  },
  methods: {
    setDefaultCurrentMonth() {
      // from/to (no date_start/date_end): son los nombres que espera
      // DrawerFilter.vue vía la prop :setFilters para poder reflejar el
      // rango activo en su propio date-picker (ver su destructuring de
      // "setFilters" — "from"/"to" es el contrato real, no uno inventado).
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
      this.searchFilters = {
        from: `${y}-${m}-01`,
        to: `${y}-${m}-${String(lastDay).padStart(2, "0")}`
      };
    },
    openForm() {
      this.editExpense = null;
      this.showForm = true;
    },
    openEdit(expense) {
      this.editExpense = expense;
      this.showForm = true;
    },
    async loadExpenses(params = {}) {
      this.loading = true;
      try {
        const res = await projectService.getExpenses(params);
        if (res.data.success) {
          this.expenses = res.data.data;
          this.totals = res.data.totals || this.totals;
        }
      } finally {
        this.loading = false;
      }
    },
    onChangeFilters(filters) {
      this.searchFilters = { ...filters };
    },
    onApplyFilters() {
      // DrawerFilter emite "onApplyFilters" SIN payload — siempre justo
      // después de "onChangeFilters" (mismo click en "Buscar"), que ya dejó
      // this.searchFilters actualizado. Antes se esperaba un argumento que
      // nunca llegaba, así que aplicar filtros manualmente no hacía nada.
      this.loadExpenses(this.buildParams(this.searchFilters));
    },
    buildParams(filters = {}) {
      // El backend (ExpenseController) espera date_from/date_to — DrawerFilter
      // entrega from/to. Antes se reenviaba date_start/date_end (ninguno de
      // los dos lados los reconoce), así que ningún filtro de fecha llegaba
      // a aplicarse nunca, ni el default de mes en curso ni uno manual.
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.from) params.date_from = filters.from;
      if (filters.to) params.date_to = filters.to;
      if (filters.order_by) params.order_by = filters.order_by;
      return params;
    },
    onExpenseSaved(expense) {
      if (expense) {
        const idx = this.expenses.findIndex(e => e.id === expense.id);
        if (idx !== -1) {
          this.$set(this.expenses, idx, expense); // edición
        } else {
          this.expenses.unshift(expense); // nuevo
        }
        this.recalcTotals();
      } else {
        this.loadExpenses(this.buildParams(this.searchFilters));
      }
    },
    recalcTotals() {
      const direct = this.expenses
        .filter(e => e.expense_type === "direct")
        .reduce((s, e) => s + Number(e.amount || 0), 0);
      const overhead = this.expenses
        .filter(e => e.expense_type === "overhead")
        .reduce((s, e) => s + Number(e.amount || 0), 0);
      this.totals = {
        direct_total: direct,
        overhead_total: overhead,
        grand_total: direct + overhead
      };
    },
    async confirmDelete(expense) {
      try {
        await this.$confirm("¿Eliminar este gasto?", "Confirmar", {
          type: "warning"
        });
        await projectService.deleteExpense(expense.id);
        this.expenses = this.expenses.filter(e => e.id !== expense.id);
        this.recalcTotals();
        this.$notifications.success({ message: "Gasto eliminado." });
      } catch (e) {
        /* cancelado */
      }
    },
    formatNum(val) {
      if (!val && val !== 0) return "0.00";
      return Number(val).toLocaleString("es-EC", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.empty-icon-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #f0faf7;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;

  i {
    font-size: 32px;
    color: #3ecfb4;
  }
}

.expenses-totals {
  margin: 8px 0 4px;
  background: #fafbff;
  border: 1px solid #eef0f8;
  border-radius: 10px;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.total-row {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #6b7280;

  &--bold {
    border-top: 1px solid #eef0f8;
    padding-top: 6px;
    margin-top: 2px;
    font-weight: 600;
    color: #2d2b55;
  }
}

.total-amount {
  font-weight: 500;
  &--red {
    color: #a32d2d;
  }
}
</style>
